import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
interface Props {
  onNext: () => void;
}
const moods = [{
  emoji: '😬',
  label: 'Uncertain',
  color: 'from-slate-600 to-slate-700'
}, {
  emoji: '😐',
  label: 'Focused',
  color: 'from-amber-600 to-orange-600'
}, {
  emoji: '🔥',
  label: 'Fired up',
  color: 'from-red-500 to-red-700'
}];
export const Screen2Slider = ({
  onNext
}: Props) => {
  const [value, setValue] = useState(50);
  const [interacted, setInteracted] = useState(false);
  const moodIndex = value < 33 ? 0 : value < 66 ? 1 : 2;
  const currentMood = moods[moodIndex];
  return <div className="relative w-full h-full bg-slate-950 overflow-hidden flex flex-col" data-id="element-314">
      {/* Background gradient that shifts with mood */}
      <motion.div animate={{
      opacity: 0.4
    }} className={`absolute inset-0 bg-gradient-to-br ${currentMood.color} transition-all duration-700 opacity-30`} data-id="element-315" />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/80 to-slate-950" data-id="element-316" />

      <div className="relative z-10 flex-1 flex flex-col p-8 pt-16" data-id="element-317">
        <motion.span initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.2
      }} className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-semibold mb-4" data-id="element-318">
          02 — Honest Check-in
        </motion.span>

        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.7
      }} className="text-3xl font-bold text-white leading-tight mb-3" data-id="element-319">
          Let’s be honest…
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.7
      }} className="text-2xl text-slate-300 font-light leading-snug mb-12" data-id="element-320">
          how does CSL feel right now?
        </motion.p>

        {/* Mood display */}
        <div className="flex-1 flex flex-col items-center justify-center" data-id="element-321">
          <AnimatePresence mode="wait" data-id="element-322">
            <motion.div key={moodIndex} initial={{
            scale: 0.6,
            opacity: 0,
            rotate: -10
          }} animate={{
            scale: 1,
            opacity: 1,
            rotate: 0
          }} exit={{
            scale: 0.6,
            opacity: 0,
            rotate: 10
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="text-8xl mb-4" data-id="element-323">
              {currentMood.emoji}
            </motion.div>
          </AnimatePresence>
          <AnimatePresence mode="wait" data-id="element-324">
            <motion.span key={currentMood.label} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="text-lg font-medium text-white tracking-wide" data-id="element-325">
              {currentMood.label}
            </motion.span>
          </AnimatePresence>
        </div>

        {/* Slider */}
        <div className="mb-8" data-id="element-326">
          <div className="relative h-12 flex items-center" data-id="element-327">
            {/* Track */}
            <div className="absolute inset-x-0 h-1.5 rounded-full bg-white/10 overflow-hidden" data-id="element-328">
              <motion.div className="h-full bg-gradient-to-r from-slate-500 via-amber-500 to-red-500 rounded-full" style={{
              width: `${value}%`
            }} data-id="element-329" />
            </div>
            <input type="range" min={0} max={100} value={value} onChange={e => {
            setValue(Number(e.target.value));
            setInteracted(true);
          }} className="absolute inset-0 w-full opacity-0 cursor-pointer z-10" data-id="element-330" />
            <div className="absolute w-6 h-6 rounded-full bg-white shadow-lg shadow-red-900/40 border-2 border-red-500 pointer-events-none" style={{
            left: `calc(${value}% - 12px)`
          }} data-id="element-331" />
          </div>
          <div className="flex justify-between mt-3 text-xs text-slate-500" data-id="element-332">
            {moods.map(m => <span key={m.label} data-id="element-333">
                {m.emoji} {m.label}
              </span>)}
          </div>
        </div>

        {interacted && <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="text-sm text-slate-400 italic text-center mb-4" data-id="element-334">
            Interesting… now let’s see what others think.
          </motion.p>}

        <button onClick={onNext} disabled={!interacted} className="w-full bg-red-600 hover:bg-red-500 disabled:bg-slate-800 disabled:text-slate-500 text-white font-semibold py-4 rounded-2xl flex items-center justify-center space-x-2 transition-all active:scale-[0.98]" data-id="element-335">
          <span data-id="element-336">Continue</span>
          <ArrowRight className="w-5 h-5" data-id="element-337" />
        </button>
      </div>
    </div>;
};